import { cn } from '@/lib/utils'
import { FxIcon } from './FxIcon'

/**
 * The list/grid switch at the right end of the tab row: two 36×32 raised
 * squares, the active one `primary-strong` with a white icon.
 *
 * Two buttons rather than one toggle, because each states what it will show
 * rather than what will change; `aria-pressed` carries which one is current,
 * since the icons alone do not.
 */
export interface FxViewToggleProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  value?: 'list' | 'grid'
  onChange?: (value: 'list' | 'grid') => void
}

const views = [
  { value: 'list', icon: 'format_list_bulleted', label: 'Listenansicht' },
  { value: 'grid', icon: 'apps', label: 'Gridansicht' },
] as const

export function FxViewToggle({ value = 'grid', onChange, className, ...props }: FxViewToggleProps) {
  return (
    <div
      role="group"
      aria-label="Ansicht"
      className={cn('inline-flex gap-4', className)}
      {...props}
    >
      {views.map((view) => {
        const isActive = view.value === value
        return (
          <button
            key={view.value}
            type="button"
            aria-label={view.label}
            aria-pressed={isActive}
            onClick={() => onChange?.(view.value)}
            className={cn(
              'inline-flex h-tab w-9 items-center justify-center rounded-sm shadow-raised',
              isActive ? 'bg-primary-strong text-on-primary' : 'bg-fill text-text',
            )}
          >
            <FxIcon name={view.icon} size={22} />
          </button>
        )
      })}
    </div>
  )
}
